import type { MediaState } from '@anisync/types';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { dismissToast, toast } from '../components/ui/toast';
import { ApiError, startPlay } from './api';
import { trackMedia } from './media-tracker';

export interface StartEpisodeInput {
  titleId: string;
  episode: number;
  /** „Frieren — odc. 9" — tytuł powiadomienia. */
  label: string;
  prefer?: {
    sourceId?: string;
    preferHost?: string;
    preferEmbedUrl?: string;
    preferAudio?: 'subs' | 'dub' | 'any';
  };
  /** Stan z listy odcinków, gdy wołający go zna — zmienia treść potwierdzenia. */
  knownState?: MediaState | null;
}

/**
 * Uruchomienie odcinka z potwierdzeniem, które widać od pierwszej chwili.
 *
 * Zgłoszenie z 12.10.2026: od dołączenia Shinden zebranie kopii trwa do pół
 * minuty, a kliknięcie „Odtwórz" nie dawało przez ten czas żadnego znaku. Serwer
 * odpowiada teraz od razu, a ten hak pokazuje powiadomienie JESZCZE PRZED
 * odpowiedzią — i oddaje je śledzeniu, które prowadzi je aż do „Oglądaj".
 *
 * Gotowy materiał nie dostaje komunikatu: obraz rusza natychmiast i to jest
 * wystarczające potwierdzenie.
 *
 * Zwraca `true`, gdy przejście do odtwarzacza się odbyło — wołający może wtedy
 * zdjąć własny stan „zaczynam".
 */
export function useStartEpisode(): (input: StartEpisodeInput) => Promise<boolean> {
  const navigate = useNavigate();

  return useCallback(
    async (input: StartEpisodeInput): Promise<boolean> => {
      const toastId = `start:${input.titleId}:${input.episode}`;
      const pending =
        input.knownState != null && input.knownState !== 'ready' && input.knownState !== 'failed';

      if (input.knownState !== 'ready') {
        toast({
          id: toastId,
          tone: 'loading',
          title: input.label,
          description: pending
            ? 'Ten odcinek już się przygotowuje — otwieram odtwarzacz.'
            : 'Szukam kopii w źródłach…',
          progress: null,
        });
      }

      try {
        const started = await startPlay(input.titleId, input.episode, input.prefer);
        const href = `/odtwarzacz/${started.mediaFileId}?tytul=${input.titleId}&odcinek=${input.episode}`;

        if (started.state === 'ready') {
          dismissToast(toastId);
        } else {
          /**
           * Komunikat zostaje kilka sekund także w odtwarzaczu — to on jest
           * potwierdzeniem kliknięcia — i mówi, że z odtwarzacza wolno wyjść.
           * Gdy widz wyjdzie, śledzenie podmieni treść na bieżący etap.
           */
          toast({
            id: toastId,
            tone: 'loading',
            title: input.label,
            description: pending
              ? 'Odcinek się przygotowuje. Możesz wrócić do listy — dam znać, gdy będzie gotowy.'
              : 'Szukam kopii w źródłach. Możesz wrócić do listy — dam znać, gdy będzie gotowy.',
            progress: null,
            durationMs: 6000,
          });
          trackMedia({ mediaFileId: started.mediaFileId, label: input.label, href, toastId });
        }

        navigate(href);
        return true;
      } catch (e: unknown) {
        toast({
          id: toastId,
          tone: 'error',
          title: input.label,
          description: e instanceof ApiError ? e.message : 'Nie udało się rozpocząć odtwarzania',
          durationMs: 8000,
        });
        return false;
      }
    },
    [navigate],
  );
}
